import React, { useEffect, useRef, useState } from 'react';
import {
    Box,
    Typography,
    Paper,
    IconButton,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import CloseIcon from '@mui/icons-material/Close';
import ReportIcon from '@mui/icons-material/Report';
import toast from 'react-hot-toast';

interface WebhookEvent {
    id: string;
    municipio: string;
    camada: string;
    event?: string;
    method?: string;
    status: 'loading' | 'success' | 'error';
    message: string;
    timestamp: number;
    error?: string;
    isAtributos?: boolean;
    retryPayload?: any;
}

export default function WebhookStatusPanel({ socket, onRetry }: { socket: any; onRetry?: (event: WebhookEvent) => void; }) {
    const [events, setEvents] = useState<WebhookEvent[]>([]);
    const [selectedError, setSelectedError] = useState<string | null>(null);
    const [reportingIds, setReportingIds] = useState<Set<string>>(new Set());
    const startedAtRef = useRef<Record<string, number>>({});
    const finishedDurationRef = useRef<Record<string, number>>({});
    const [now, setNow] = useState(Date.now());
    const getEventKey = (event: WebhookEvent) => `${event.id}-${event.event || event.camada}-${event.municipio}-${event.method || ''}-${event.isAtributos === true ? 'attr' : 'norm'}`;
    const hasPendingRequests = events.some((event) => event.status === 'loading');

    useEffect(() => {
        if (!hasPendingRequests) return;
        const interval = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(interval);
    }, [hasPendingRequests]);

    useEffect(() => {
        if (!socket) return;

        socket.on('webhookStatus', (event: Omit<WebhookEvent, 'timestamp'>) => {

            const eventTimestamp = Date.now();
            const newEvent = { ...event, timestamp: eventTimestamp };
            const key = getEventKey(newEvent);

            if (newEvent.status === 'loading' || !startedAtRef.current[key]) {
                startedAtRef.current[key] = eventTimestamp;
                delete finishedDurationRef.current[key];
            }

            if ((newEvent.status === 'success' || newEvent.status === 'error') && startedAtRef.current[key]) {
                finishedDurationRef.current[key] = eventTimestamp - startedAtRef.current[key];
            }

            setEvents((prev) => {
                const alreadyExists = prev.some((e) => getEventKey(e) === key);
                const updated = alreadyExists
                    ? prev.map((e) =>
                        getEventKey(e) === key
                            ? newEvent
                            : e
                    )
                    : [...prev, newEvent];

                return updated;
            });

        });

        return () => {
            socket.off('webhookStatus');
        };
    }, [socket]);

    function removeEvent(event: WebhookEvent) {
        const key = getEventKey(event);
        setEvents((prev) =>
            prev.filter((e) => getEventKey(e) !== key)
        );
        delete startedAtRef.current[key];
        delete finishedDurationRef.current[key];
    }

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'success':
                return <CheckCircleIcon color="success" sx={{ transition: 'all 0.3s ease' }} />;
            case 'error':
                return <ErrorIcon color="error" sx={{ transition: 'all 0.3s ease' }} />;
            default:
                return <HourglassBottomIcon color="primary" sx={{ transition: 'all 0.3s ease' }} />;
        }
    };

    const getBorderColor = (status: string) => {
        switch (status) {
            case 'success':
                return '#4caf50';
            case 'error':
                return '#f44336';
            default:
                return '#2196f3';
        }
    };

    return (
        <>
            <Box
                sx={{
                    position: 'fixed',
                    top: 100,
                    right: 16,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    zIndex: 9999,
                }}
            >
                {events.map((event) => {
                    const eventKey = getEventKey(event);
                    const startedAt = startedAtRef.current[eventKey];
                    const finishedDuration = finishedDurationRef.current[eventKey];
                    const durationMs = typeof finishedDuration === 'number'
                        ? finishedDuration
                        : (startedAt ? Math.max(0, now - startedAt) : undefined);
                    const isFinished = event.status === 'success' || event.status === 'error';
                    const durationLabel = typeof durationMs === 'number'
                        ? `${isFinished ? 'Tempo total' : 'Tempo decorrido'}: ${formatDuration(durationMs)}`
                        : null;
                    const isReporting = reportingIds.has(eventKey);
                    const sinterInfo = getSinterInfo(event);
                    const displayMessage = sinterInfo
                        ? getSinterMessage(event.status)
                        : event.message;
                    const displayDetails = sinterInfo
                        ? `${sinterInfo.municipio} • ${sinterInfo.camada}`
                        : `${event.municipio} • ${event.camada}`;

                    return (
                        <Paper
                            key={eventKey}
                            elevation={6}
                            sx={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: 2,
                                p: 2,
                                minWidth: 320,
                                borderLeft: `6px solid ${getBorderColor(event.status)}`,
                                animation: 'fadeIn 0.3s ease',
                                transition: 'all 0.3s ease',
                            }}
                        >
                            <Box mt={0.5}>{getStatusIcon(event.status)}</Box>

                            <Box
                                sx={{
                                    flex: 1,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 1,
                                }}
                            >
                                <Typography fontWeight="bold" fontSize="0.95rem">
                                    {displayMessage}
                                </Typography>

                                <Typography variant="caption" color="text.secondary">
                                    {displayDetails}
                                </Typography>

                                {durationLabel && (
                                    <Typography variant="caption" color="text.secondary">
                                        {durationLabel}
                                    </Typography>
                                )}

                                {event.status === 'error' && event.error && (
                                    <>
                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                            <Button
                                                size="small"
                                                onClick={() => setSelectedError(event.error!)}
                                                sx={{
                                                    px: 1.5,
                                                    py: 0.5,
                                                    fontSize: '0.75rem',
                                                    textTransform: 'none',
                                                    backgroundColor: '#fdecea',
                                                    color: '#d32f2f',
                                                    '&:hover': { backgroundColor: '#f9d5d3' },
                                                }}
                                                startIcon={<ErrorIcon fontSize="small" />}
                                            >
                                                Ver detalhes
                                            </Button>

                                            {isReporting ? (
                                                <Button
                                                    size="small"
                                                    disabled
                                                    startIcon={<CheckCircleIcon fontSize="small" />}
                                                    sx={{
                                                        px: 1.5,
                                                        py: 0.5,
                                                        fontSize: '0.75rem',
                                                        textTransform: 'none',
                                                        color: '#4caf50',
                                                        backgroundColor: 'transparent',
                                                        '&:hover': {
                                                            backgroundColor: 'transparent',
                                                        },
                                                    }}
                                                >
                                                    Reportado
                                                </Button>
                                            ) : (
                                                <Button
                                                    size="small"
                                                    onClick={() => {
                                                        setReportingIds(prev => new Set(prev).add(eventKey));

                                                        socket.emit('sendEmail', event, (ack: { success: boolean; message?: string }) => {
                                                            if (ack?.success) {
                                                                toast.success("Reportado com sucesso!");
                                                            } else {
                                                                toast.error("Não foi possível reportar o erro. Entre em contato com o suporte.");
                                                                setReportingIds(prev => {
                                                                    const newSet = new Set(prev);
                                                                    newSet.delete(eventKey);
                                                                    return newSet;
                                                                });
                                                            }
                                                        });

                                                    }}
                                                    sx={{
                                                        px: 1.5,
                                                        py: 0.5,
                                                        fontSize: '0.75rem',
                                                        textTransform: 'none',
                                                        backgroundColor: '#fff3e0',
                                                        color: '#ef6c00',
                                                        '&:hover': { backgroundColor: '#ffe0b2' },
                                                    }}
                                                    startIcon={<ReportIcon fontSize="small" />}
                                                >
                                                    Reportar
                                                </Button>
                                            )}

                                        </Box>

                                        <Button
                                            size="small"
                                            onClick={() => {
                                                if (event.retryPayload) {
                                                    socket.emit('startWebhook', {
                                                        ...event.retryPayload,
                                                        ...(event.isAtributos === true && event.municipio?.toLowerCase() !== 'vitoriadaconquista'
                                                            ? { attrsOnly: true }
                                                            : {})
                                                    });
                                                } else {
                                                    onRetry?.(event);
                                                }
                                            }}
                                            sx={{
                                                px: 1.5,
                                                py: 0.5,
                                                fontSize: '0.75rem',
                                                textTransform: 'none',
                                                backgroundColor: '#e3f2fd',
                                                color: '#1565c0',
                                                alignSelf: 'flex-start',
                                                '&:hover': {
                                                    backgroundColor: '#bbdefb',
                                                },
                                            }}
                                            startIcon={<HourglassBottomIcon fontSize="small" />}
                                        >
                                            Tentar novamente
                                        </Button>
                                    </>
                                )}
                            </Box>

                            <IconButton size="small" onClick={() => removeEvent(event)}>
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </Paper>
                    );
                })}

                <style>{`
                    @keyframes fadeIn {
                        from {
                        opacity: 0;
                        transform: translateY(10px);
                        }
                        to {
                        opacity: 1;
                        transform: translateY(0);
                        }
                    }
        `}</style>
            </Box>

            <Dialog open={!!selectedError} onClose={() => setSelectedError(null)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontWeight: 'bold' }}>Detalhes do Erro</DialogTitle>
                <DialogContent>
                    <Typography sx={{ whiteSpace: 'pre-wrap' }}>{selectedError}</Typography>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setSelectedError(null)}
                        autoFocus
                        color="success"
                        variant="contained"
                    >
                        Fechar
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}

function formatDuration(ms: number) {
    if (!Number.isFinite(ms) || ms < 0) {
        return '--:--:--';
    }

    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    const hh = hours.toString().padStart(2, '0');
    const mm = minutes.toString().padStart(2, '0');
    const ss = seconds.toString().padStart(2, '0');

    return `${hh}:${mm}:${ss}`;
}

function getSinterInfo(event: WebhookEvent): { municipio: string; camada: string } | null {
    const eventName = event.event || "";
    if (!eventName.toLowerCase().startsWith("sinter-")) {
        return null;
    }

    const match = eventName.match(/^sinter-([^-]+)-(.+?)(?:-(post|put|delete))?$/i);
    if (!match) {
        return null;
    }

    return {
        municipio: match[1],
        camada: match[2],
    };
}

function getSinterMessage(status: WebhookEvent["status"]): string {
    switch (status) {
        case "success":
            return "Integração SINTER concluída com sucesso";
        case "error":
            return "Erro na integração SINTER";
        default:
            return "Integração SINTER iniciada";
    }
}
